import { useState, useEffect } from 'react';
import axios from 'axios';
import { Share2, ChevronDown, ChevronUp, Award } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function FeaturedAcupunctureCard() {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedSection, setExpandedSection] = useState(null); // Start with all sections collapsed

  useEffect(() => {
    loadFeaturedCard();
  }, []);

  const loadFeaturedCard = async () => {
    try {
      const response = await axios.get(`${API}/acupuncture-cards/featured`);
      setCard(response.data);
    } catch (error) {
      console.error('Failed to load featured card:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    const shareUrl = window.location.origin;
    const shareText = `Check out this acupuncture point: ${card.point_name} (${card.channel_number})!`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `${card.point_name} - AcuErnie`,
          text: shareText,
          url: shareUrl
        });
      } catch (err) {
        console.error('Share failed:', err);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
      toast.success('Link copied to clipboard!');
    }
  };

  if (loading) return null;
  if (!card) return null;

  return (
    <section className="py-8 sm:py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-[#1A0F0A] to-[#2C1810]">
      <div className="max-w-4xl mx-auto">
        {/* Card Container - Compact & Mobile Optimized */}
        <div 
          className="cassette-card overflow-hidden relative"
          style={{
            background: 'linear-gradient(135deg, #E8934D 0%, #E85D4D 25%, #E8934D 50%, #D4722A 75%, #E8934D 100%)',
            border: '6px solid #F4E4C1',
            boxShadow: '0 20px 40px rgba(0,0,0,0.4), inset 0 2px 4px rgba(255,255,255,0.3)'
          }}
        >
          {/* Decorative stripes around entire box */}
          <div className="absolute top-0 left-0 w-full h-3 bg-gradient-to-r from-[#E8934D] via-[#E85D4D] to-[#E8934D] opacity-90"></div>
          <div className="absolute bottom-0 left-0 w-full h-3 bg-gradient-to-r from-[#E8934D] via-[#E85D4D] to-[#E8934D] opacity-90"></div>
          <div className="absolute left-0 top-0 h-full w-3 bg-gradient-to-b from-[#E8934D] via-[#E85D4D] to-[#E8934D] opacity-90"></div>
          <div className="absolute right-0 top-0 h-full w-3 bg-gradient-to-b from-[#E8934D] via-[#E85D4D] to-[#E8934D] opacity-90"></div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            {/* Header with Large Logo */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 sm:gap-4 mb-4 bg-[#F4E4C1] rounded-xl p-3 sm:p-4 shadow-lg">
              <div className="flex items-center gap-3 sm:gap-4 flex-1 w-full">
                <img 
                  src="/logo_acuernie.png" 
                  alt="AcuErnie Logo" 
                  className="w-20 h-20 sm:w-28 sm:h-28 md:w-36 md:h-36 object-contain drop-shadow-2xl flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl sm:text-2xl md:text-3xl retro-title text-[#2C1810] leading-tight mb-1 truncate">
                    {card.point_name}
                  </h2>
                  <p className="text-base sm:text-lg md:text-xl font-black text-[#E85D4D] mb-2">
                    {card.channel_number}
                  </p>
                  <Link
                    to="/testing-center/basic/acupuncture"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-[#E85D4D] hover:bg-[#D4722A] text-white text-xs sm:text-sm font-bold rounded-lg transition-all shadow-md"
                  >
                    <Award className="w-4 h-4" />
                    Take The Test
                  </Link>
                </div>
              </div>
              <button
                onClick={handleShare}
                className="p-3 bg-[#E85D4D] text-white rounded-full hover:bg-[#D4722A] transition-all shadow-lg flex-shrink-0"
                title="Share this card"
              >
                <Share2 className="w-5 h-5" />
              </button>
            </div>

            {/* Fun Fact - Compact */}
            <div className="mb-4 p-3 sm:p-4 bg-[#F4E4C1] rounded-lg border-3 border-[#2C1810] shadow-lg">
              <p className="text-sm sm:text-base md:text-lg font-black text-[#2C1810] leading-snug text-center">
                ✨ {card.fun_fact}
              </p>
            </div>

            {/* SECTION 1: Essential Info (Always visible, collapsible) */}
            <div className="mb-3">
              <button
                onClick={() => setExpandedSection(expandedSection === 1 ? 0 : 1)}
                className="w-full flex items-center justify-between p-3 bg-[#F4E4C1] rounded-lg shadow-md hover:bg-[#E8B67A] transition-all"
              >
                <span className="text-base sm:text-lg font-black text-[#2C1810]">
                  📍 Essential Information
                </span>
                {expandedSection === 1 ? (
                  <ChevronUp className="w-5 h-5 text-[#2C1810]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#2C1810]" />
                )}
              </button>
              {expandedSection === 1 && (
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3 animate-in slide-in-from-top duration-300">
                  <div className="bg-[#F4E4C1] p-3 rounded-lg shadow">
                    <h3 className="text-sm sm:text-base font-black text-[#2C1810] mb-2">Actions</h3>
                    <p className="text-[#3D2817] leading-relaxed text-xs sm:text-sm">{card.actions}</p>
                  </div>
                  <div className="bg-[#F4E4C1] p-3 rounded-lg shadow">
                    <h3 className="text-sm sm:text-base font-black text-[#2C1810] mb-2">Indications</h3>
                    <p className="text-[#3D2817] leading-relaxed text-xs sm:text-sm">{card.indications}</p>
                  </div>
                  <div className="bg-[#F4E4C1] p-3 rounded-lg shadow sm:col-span-2">
                    <h3 className="text-sm sm:text-base font-black text-[#2C1810] mb-2">Location</h3>
                    <p className="text-[#3D2817] leading-relaxed text-xs sm:text-sm">{card.location}</p>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 2: Technique & Practice (Collapsible) */}
            <div className="mb-3">
              <button
                onClick={() => setExpandedSection(expandedSection === 2 ? 0 : 2)}
                className="w-full flex items-center justify-between p-3 bg-[#F4E4C1] rounded-lg shadow-md hover:bg-[#E8B67A] transition-all"
              >
                <span className="text-base sm:text-lg font-black text-[#2C1810]">
                  ⚡ Technique & Practice
                </span>
                {expandedSection === 2 ? (
                  <ChevronUp className="w-5 h-5 text-[#2C1810]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#2C1810]" />
                )}
              </button>
              {expandedSection === 2 && (
                <div className="mt-2 grid grid-cols-1 gap-3 animate-in slide-in-from-top duration-300">
                  {card.image_url && (
                    <div className="bg-[#F4E4C1] p-2 rounded-lg shadow">
                      <img 
                        src={card.image_url} 
                        alt={card.point_name}
                        className="w-full h-auto rounded"
                      />
                    </div>
                  )}
                  <div className="bg-[#F4E4C1] p-3 rounded-lg shadow">
                    <h3 className="text-sm sm:text-base font-black text-[#2C1810] mb-2">Needling Technique</h3>
                    <p className="text-[#3D2817] leading-relaxed text-xs sm:text-sm">{card.needling_technique}</p>
                  </div>
                  <div className="bg-[#F4E4C1] p-3 rounded-lg shadow">
                    <h3 className="text-sm sm:text-base font-black text-[#2C1810] mb-2">Common Combinations</h3>
                    <p className="text-[#3D2817] leading-relaxed text-xs sm:text-sm">{card.common_combinations}</p>
                  </div>
                </div>
              )}
            </div>

            {/* SECTION 3: Advanced Insights (Collapsible) */}
            <div className="mb-2">
              <button
                onClick={() => setExpandedSection(expandedSection === 3 ? 0 : 3)}
                className="w-full flex items-center justify-between p-3 bg-[#F4E4C1] rounded-lg shadow-md hover:bg-[#E8B67A] transition-all"
              >
                <span className="text-base sm:text-lg font-black text-[#2C1810]">
                  🧠 Advanced Insights
                </span>
                {expandedSection === 3 ? (
                  <ChevronUp className="w-5 h-5 text-[#2C1810]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#2C1810]" />
                )}
              </button>
              {expandedSection === 3 && (
                <div className="mt-2 space-y-3 animate-in slide-in-from-top duration-300">
                  <div className="bg-[#F4E4C1] p-3 rounded-lg shadow">
                    <h3 className="text-sm sm:text-base font-black text-[#2C1810] mb-2">Commentary</h3>
                    <p className="text-[#3D2817] leading-relaxed text-xs sm:text-sm">{card.commentary}</p>
                  </div>
                  {card.spirit_of_point && (
                    <div className="bg-gradient-to-r from-purple-100 to-purple-200 p-3 rounded-lg shadow border-2 border-purple-400">
                      <h3 className="text-sm sm:text-base font-black text-purple-900 mb-2">Spirit of the Point</h3>
                      <p className="text-purple-900 leading-relaxed text-xs sm:text-sm italic">{card.spirit_of_point}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
